// ─── IMPORT BASE ───────────────────────────────────────────────
import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { usePreventivo } from '../hooks/usePreventivo';
import { useAuth } from '../context/AuthContext';

import imageCompression from 'browser-image-compression';
import { getCurrentLocation, getCityFromCoords } from './geolocation';

import logo from '../logo-infissi.gif';
import coin from '../coin-emoji.png';

import imgF1A from '../Immagini/F1AdxBia.png';
import imgF2A from '../Immagini/F2AdxBIA.png';
import imgF3A from '../Immagini/F3AdxBIA.png';
import imgPF1A from '../Immagini/PF1AdxBia.png';
import imgPF2A from '../Immagini/PF2AdxBIA.png';
import imgPF3A from '../Immagini/PF3AdxBIA.png';
import imgPortoncino from '../Immagini/PORTONCINOBIA.png';
import imgBlindato from '../Immagini/BLINDATO.png';
import imgScorrevole from '../Immagini/SCORREVOLE.png';
import imgFIX from '../Immagini/FIXBIA.png';
import imgPorta from '../Immagini/porta.png';

import imgCassonetto from '../Immagini/cassonetto.png';
import imgTapparella from '../Immagini/tapparella.png';
import imgZanzariera from '../Immagini/zanzariera.png';
import imgScuri from '../Immagini/scuri.png';
import imgPersiana from '../Immagini/persiana.png';
import triploVetroImage from '../Immagini/triplo_vetro.png';

import { fetchListino, priceLine, pricePreventivo } from '../utils/pricingFromDb';
import './nuovo.css';


// ───────────────────────────────────────────────────────────────
//   gestione tipi infisso (normalizzazione + meta)
// ───────────────────────────────────────────────────────────────

// Chiave tecnica per look-up/pricing (mai mostrata)
export const tipoKeyOf = (t) =>
  String(t ?? '')
    .normalize('NFKC')
    .trim()
    .replace(/[\s._-]+/g, '')
    .toUpperCase();

// Registro base per etichette "amichevoli" di alcune famiglie
const TYPE_META_BASE = {
  F1A: { label: 'F1A · 1 anta', family: 'F', ante: 1, icon: 'f1a' },
  F2A: { label: 'F2A · 2 ante', family: 'F', ante: 2, icon: 'f2a' },
  F3A: { label: 'F3A · 3 ante', family: 'F', ante: 3, icon: 'f3a' },
  F4A: { label: 'F4A · 4 ante', family: 'F', ante: 4, icon: 'f4a' },
};

// Meta del tipo: PRESERVA sempre la grafia salvata per label/value
export function getTypeMeta(rawType) {
  const raw = String(rawType ?? '').trim();   // ← come scritto in DB
  const key = tipoKeyOf(raw);                 // ← chiave tecnica

  if (TYPE_META_BASE[key]) {
    return { type: raw, tipoKey: key, ...TYPE_META_BASE[key] };
  }

  const m = key.match(/^F(\d+)A$/i);
  if (m) {
    const ante = parseInt(m[1], 10);
    return {
      type: raw,
      tipoKey: key,
      label: `${raw} · ${ante} ante`, // usa la grafia originale
      family: 'F',
      ante,
      icon: 'f-window',
    };
  }

  // Generico: mostra esattamente ciò che è stato scritto
  return { type: raw, tipoKey: key, label: raw || key, family: '?', ante: null, icon: 'f-window' };
}

function TypeBadge({ type }) {
  const meta = getTypeMeta(type);
  return (
    <span className="type-badge" data-type={meta.tipoKey}>
      {meta.label}
    </span>
  );
}


/* (facoltativo) stile essenziale per il badge:
.type-badge { padding:.15rem .4rem; border:1px solid #ccc; border-radius:.35rem; font-weight:600; font-size:.85rem; }
*/


// ───────────────────────────────────────────────────────────────
//   JWT helpers + token URL helpers
// ───────────────────────────────────────────────────────────────

// Endpoint base delle API del preventivatore
export const API_PREV = '/preventivi/api';

// helpers per decodificare un JWT/Base64URL in modo robusto
export function b64urlDecode(str = '') {
  const s = String(str).replace(/-/g, '+').replace(/_/g, '/');
  const pad = (4 - (s.length % 4)) % 4;
  return atob(s + '='.repeat(pad));
}

export function safeDecodeJwt(tok) {
  if (!tok) return {};
  try {
    // JWT (header.payload.signature)
    const parts = String(tok).split('.');
    if (parts.length >= 2) return JSON.parse(b64urlDecode(parts[1] || ''));
  } catch {}
  try {
    // fallback: semplice base64 di JSON
    return JSON.parse(atob(tok));
  } catch {}
  return {};
}

// Legge ?t= o ?token= sia dalla query sia dopo l’hash (#/nuovo?t=...)
export function getTokenFromUrl() {
  // 1) query classica
  const qs = new URLSearchParams(window.location.search);
  let t = qs.get('t') || qs.get('token');
  if (t) return t;

  // 2) HashRouter (#/nuovo?t=...)
  const hash = window.location.hash || '';
  const qpos = hash.indexOf('?');
  if (qpos >= 0) {
    const hqs = new URLSearchParams(hash.substring(qpos + 1));
    t = hqs.get('t') || hqs.get('token');
  }
  return t || '';
}

// Restituisce il token SOLO per la sync verso Agenda.
// Se nell'URL non c'è più, non usiamo vecchi token a caso.
export function getTokenForSync() {
  try {
    const t = getTokenFromUrl();
    if (t) return t;
    // se vuoi mantenere la sync finché resti nella stessa pagina appuntamento,
    // puoi leggere da localStorage. Qui la lasciamo vuota di proposito
    // quando arrivi da Navbar (bootstrap lo ripulisce).
    return localStorage.getItem('preventivo.jwt') || '';
  } catch {
    return '';
  }
}


// ───────────────────────────────────────────────────────────────
//   Helpers vari (fuori dal componente principale)
// ───────────────────────────────────────────────────────────────

const clampDigit = (v, max) => {
  const d = String(v ?? '').replace(/\D/g, '').slice(0, 1);
  if (d === '') return '';
  const n = parseInt(d, 10);
  return String(Math.min(n, max));
};

// FK helper: 0/NaN/stringa vuota -> null, altrimenti intero > 0
export const toNullableInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// Capsula accessorio con stesso markup delle "Colori"
function AccessoryCapsule({ className, img, value, max, onChange }) {
  return (
    <div className={`btn-wrapper accessory ${className || ''}`}>
      <label
        className="sbi"
        style={{
          backgroundImage: `url("${img}")`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'left center',
        }}
      >
        {/* niente frecce; mobile mostra tastierino numerico */}
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          className="sbi-field"
          value={value ?? ''}
          placeholder="0"
          onChange={(e) => onChange(clampDigit(e.target.value, max))}
        />
      </label>
    </div>
  );
}

// DEBUG: ErrorBoundary per evitare schermo bianco e mostrare l'errore
class UiErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('[UI CRASH]', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            padding: 16,
            margin: 16,
            border: '2px solid #c00',
            borderRadius: 8,
            color: '#c00',
            background: '#fff',
          }}
        >
          {String(this.state.error)}
        </pre>
      );
    }
    return this.props.children;
  }
}

console.log('✅ Nuovo.js caricato!');

// uid per finestre
const uid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);




// --- helper per incrementare il nome finestra ---
const bumpName = (s) => {
  const name = String(s || '').trim();
  if (!name) return '';
  const m = name.match(/^(.*?)(\d+)\s*$/);
  if (m) return `${m[1].trim()}${parseInt(m[2], 10) + 1}`;
  return `${name}2`;
};

// --- clona una finestra: nuovo id, nessuna foto, nome incrementato ---
const cloneWindow = (w) => {
  const c = JSON.parse(JSON.stringify(w || {}));
  c.id = uid();
  c.photos = [];
  if (c.nomeInfisso) c.nomeInfisso = bumpName(c.nomeInfisso);
  return c;
};








// Stato “finestra” iniziale
const INITIAL_WINDOW = () => ({
  id: uid(),
  type: 'F1A',
  quantity: 1,
  zona: 'E',
  color_id: null,   // FK verso tabella colors
  tapp: false,
  cas: false,
  col2_id: null,    // secondo colore opzionale
  alt: '',
  lar: '',
  scuri: '',
  pers: '',
  zanza: 0,
  allar: '',
  photos: [],
  nomeInfisso: '',
});








// helper immagini per tipo (usa gli import!)
const getImageForType = (t) =>
  ({
    F1A: imgF1A,
    F2A: imgF2A,
    F3A: imgF3A,
    PF1A: imgPF1A,
    PF2A: imgPF2A,
    PF3A: imgPF3A,
    Portoncino: imgPortoncino,
    Blindato: imgBlindato,
    Scorrevole: imgScorrevole,
    FIX: imgFIX,
    Porta: imgPorta,
  }[t] || null);

// ─── Colori & util ─────────────────────────────────────────────

// Trova un colore per id nell'array dei colori restituiti da get_colors.php
function getColorById(colors, id) {
  return colors.find((c) => c.id === Number(id)) || null;
}

/**
 * Anteprima colore:
 * - se code inizia con "#" → riquadro colorato
 * - altrimenti carica l’immagine /dec-swatches/DEC-xxx.png
 */
function getColorPreviewElement(code, options = { fullWidth: false, noBorder: false }) {
  if (!code) return null;

  const styleBase = options.fullWidth
    ? {
        width: '100%',
        height: '100%',
        border: options.noBorder ? 'none' : '1px solid #ccc',
        borderRadius: '4px',
        boxSizing: 'border-box',
        objectFit: 'cover',
      }
    : {
        width: '40px',
        height: '40px',
        border: options.noBorder ? 'none' : '1px solid #ccc',
        borderRadius: '4px',
        boxSizing: 'border-box',
        objectFit: 'cover',
      };

  if (code.startsWith('#')) {
    return <div style={{ ...styleBase, backgroundColor: code }} />;
  }

  return <img src={`${process.env.PUBLIC_URL}/dec-swatches/${code}.png`} alt={code} style={styleBase} />;
}

// helper src foto (accetta assoluto/relativo)
function getPhotoSrc(p) {
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return p;     // URL assoluto
  if (p.startsWith('/')) return p;           // già path assoluto
  return `/preventivi/${String(p).replace(/^\/+/, '')}`; // path relativo base
}



// === Normalizzazione numeri in formato IT/EN ===
// === Normalizzazione numeri in formato IT/EN ===
const toNumber = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const s = String(v ?? '')
    .replace(/\s+/g, '')
    .replace(/\./g, '')
    .replace(/,/g, '.');
  const n = parseFloat(s.replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const toInt = (v) => Math.round(toNumber(v));



// ───────────────────────────────────────────────────────────────
//   COMPONENTE PRINCIPALE
// ───────────────────────────────────────────────────────────────
export default function Nuovo() {
  const auth = useAuth();
  console.log('[AUTH CONTEXT]', auth);   // 👈 debug per vedere cosa c’è dentro

  const { token, nome: nomeUtente, logout } = auth;




useEffect(() => {
  try {
    const authPayload = safeDecodeJwt(token);
    console.log('[AUTH TOKEN PAYLOAD]', authPayload);

    const urlTok = getTokenFromUrl();
    if (urlTok) {
      console.log('[URL TOKEN PAYLOAD]', safeDecodeJwt(urlTok));
    }
  } catch (e) {
    console.error('Errore decodificando i token', e);
  }
}, [token]);






  const { id: idPreventivoFromURL } = useParams();
  const navigate = useNavigate();

  // id + possibile nome rilevatore dal JWT dell'auth
  let idRilevatore = 0;
  let nomeRilevatoreFromAuth = '';
  try {
    const payload = safeDecodeJwt(token);
    idRilevatore = Number(payload?.id) || 0;

    // prova diversi nomi possibili nel token
    nomeRilevatoreFromAuth =
      payload?.rilevatore_nome ||
      payload?.nome_rilevatore ||
      payload?.nome ||
      payload?.username ||
      payload?.user ||
      '';
  } catch (e) {
    console.warn('[NUOVO] decode token auth fallita', e);
  }

  // nome "di base" del rilevatore:
  // 1) nome dal contesto Auth
  // 2) altrimenti nome trovato nel JWT
  const defaultRilevatoreNome =
    (nomeUtente && nomeUtente.trim()) ||
    (nomeRilevatoreFromAuth && nomeRilevatoreFromAuth.trim()) ||
    '';

  // state base
  const [preventivoId, setPreventivoId] = useState(
    idPreventivoFromURL ? Number(idPreventivoFromURL) : 0
  );
  const [rilevatoreSelezionato, setRilevatoreSelezionato] =
    useState(defaultRilevatoreNome);
  const [colors, setColors] = useState([]);


  const captureRef = useRef(null);
  const fileInputs = useRef([]);

  // dati preventivo
  const {
    cliente,
    windows,
    extraCosts,
    setWindows,
    setExtraCosts,
    loading,
    error,
  } = usePreventivo(preventivoId, rilevatoreSelezionato);

  // anagrafica editabile
  const [nomeCliente, setNomeCliente] = useState('');
  const [localitaCliente, setLocalitaCliente] = useState('');
  const [telefono, setTelefono] = useState('');
  const [note, setNote] = useState('');
  const [indirizzoCliente, setIndirizzoCliente] = useState('');

  // legami Agenda/Eventi (riempiti da token URL)
  const [linkage, setLinkage] = useState({
    eventId: null,
    zonaId: null,
    rilevatoreId: null, // può arrivare dal token; fallback a idRilevatore
  });

  // id rilevatore effettivo (null se non valido)
  const effectiveRilevatoreId = useMemo(() => {
    const v = linkage?.rilevatoreId ?? idRilevatore;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [linkage?.rilevatoreId, idRilevatore]);

  // Bootstrap da token (?t=... / ?token=...)
  
  
    useEffect(() => {
    const tokInUrl = getTokenFromUrl();

    // Caso NAVBAR: nessun token → preventivo "libero"
    if (!tokInUrl) {
      try { localStorage.removeItem('preventivo.jwt'); } catch {}
      setLinkage(prev => ({
        eventId: null,
        zonaId: null,
        rilevatoreId: idRilevatore || null,
      }));
      // se non arrivo da un appuntamento, uso il nome di base
      setRilevatoreSelezionato(defaultRilevatoreNome);
      return;
    }

    // Caso AGENDA: token presente → salva e prefilla
    const p = safeDecodeJwt(tokInUrl) || {};
    try { localStorage.setItem('preventivo.jwt', tokInUrl); } catch {}

    setLinkage(prev => ({
      eventId:      p.event_id ?? p.id_evento ?? prev.eventId ?? null,
      zonaId:       p.zona_id  ?? prev.zonaId  ?? null,
      rilevatoreId: p.rilevatore_id ?? prev.rilevatoreId ?? (idRilevatore || null),
    }));

    setNomeCliente(v => v || p?.cliente?.nome    || p.nome   || p.nome_cliente || '');
    setLocalitaCliente(v => v || p?.cliente?.comune || p.comune || p.citta || '');
    setTelefono(v => v || p?.cliente?.telefono || p.telefono || '');
    setNote(v => v || p.note || '');

    // se il token dell'agenda contiene il nome del rilevatore, lo uso.
    // altrimenti lascio quello di base (utente loggato / JWT auth)
    setRilevatoreSelezionato(
      p.rilevatore_nome ||
      p.nome_rilevatore ||
      defaultRilevatoreNome
    );

    // pulizia parametri URL
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('t'); u.searchParams.delete('token');
      if (u.hash && u.hash.includes('?')) {
        const [hashPath, hashQs] = u.hash.split('?');
        const hqs = new URLSearchParams(hashQs);
        hqs.delete('t'); hqs.delete('token');
        u.hash = hqs.toString() ? `${hashPath}?${hqs}` : hashPath;
      }
      window.history.replaceState({}, '', u.toString());
    } catch {}
  }, [idRilevatore, nomeUtente]);

  // Quando arrivano i dati dal DB, completa i campi mancanti
    useEffect(() => {
    if (!cliente) return;
    setNomeCliente(v => v || cliente.nome || cliente.nome_cliente || '');
    setLocalitaCliente(v => v || cliente.localita || cliente.comune || '');
    setTelefono(v => v || cliente.telefono || cliente.cliente_telefono || '');
    setNote(v => v || cliente.note || '');

    // se il DB ha salvato un nome_rilevatore lo uso,
    // altrimenti NON sovrascrivo il nome che ho già
    setRilevatoreSelezionato(prev =>
      prev ||
      cliente.nome_rilevatore ||
      defaultRilevatoreNome
    );
  }, [cliente, defaultRilevatoreNome]);

  // GEOLOC (📍)
  const handleGeolocalizza = async () => {
    try {
      const pos = await getCurrentLocation();
      const { latitude, longitude } = pos.coords;
      const luogo = await getCityFromCoords(latitude, longitude);
      setLocalitaCliente(luogo || 'Posizione sconosciuta');
    } catch (err) {
      alert('Errore geolocalizzazione: ' + err.message);
    }
  };

  // Supporto: colori
  useEffect(() => {
    let alive = true;
    fetch('/preventivi/get_colors.php', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((json) => {
        if (!alive) return;
        if (json?.status === 'OK' && Array.isArray(json.colors)) {
          setColors(json.colors);
        } else if (Array.isArray(json)) {
          setColors(json);
        } else {
          console.error('get_colors.php →', json);
        }
      })
      .catch((err) => console.error('get_colors.php →', err));
    return () => { alive = false; };
  }, []);

  // Supporto: listino
  const [listino, setListino] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchListino()
      .then(L => { if (alive) setListino(L); })
      .catch(e => alert(e.message || 'Errore caricando listino'));
    return () => { alive = false; };
  }, []);

  // Opzioni "Tipo" dal listino (fallback statico)
  const tipoOptions = useMemo(() => {
    const map = new Map();

    if (listino?.baseByTipo) {
      Object.values(listino.baseByTipo).forEach((rec) => {
        const label = String(rec?.tipo ?? '').trim();
        if (!label) return;
        const k = tipoKeyOf(label);
        if (!map.has(k)) map.set(k, label); // dedup su chiave tecnica
      });
    }

    if (map.size === 0) {
      ['F1A','F2A','F3A','PF1A','PF2A','PF3A','Portoncino','Blindato','Scorrevole','FIX','Porta']
        .forEach((t) => map.set(tipoKeyOf(t), t));
    }

    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'it', { numeric: true }));
  }, [listino]);

  // Raggruppo opzioni per famiglia
  const groupedTipoOptions = useMemo(() => {
    const g = { 'Finestre': [], 'Porte-finestre': [], 'Porte': [], 'Altro': [] };
    (tipoOptions || []).forEach((t) => {
      const T = String(t).toUpperCase();
      if (T === 'FIX' || /^F\d+A$/.test(T)) g['Finestre'].push(t);
      else if (T === 'SCORREVOLE' || /^PF\d+A$/.test(T)) g['Porte-finestre'].push(t);
      else if (T === 'PORTA' || T === 'PORTONCINO' || T === 'BLINDATO') g['Porte'].push(t);
      else g['Altro'].push(t);
    });
    Object.keys(g).forEach((k) => { if (!g[k].length) delete g[k]; });
    return g;
  }, [tipoOptions]);

  // Immagine di preview per tipo (mappa statica → URL listino)
  const previewImg = useCallback((t) => {
    const staticMap = {
      F1A: imgF1A, F2A: imgF2A, F3A: imgF3A,
      PF1A: imgPF1A, PF2A: imgPF2A, PF3A: imgPF3A,
      Portoncino: imgPortoncino, Blindato: imgBlindato, Scorrevole: imgScorrevole,
      FIX: imgFIX, Porta: imgPorta,
    };
    if (staticMap[t]) return staticMap[t];
    const rec = listino?.baseByTipo?.[tipoKeyOf(t)];
    const url = rec?.img;
    if (!url) return null;
    return (url.startsWith('http') || url.startsWith('/'))
      ? url
      : `/preventivi/${String(url).replace(/^\/+/, '')}`;
  }, [listino]);

  // ─── Handlers base finestre ──────────────────────────────────
  const updateField = useCallback((idx, field, value) => {
    setWindows((prev) => prev.map((el, i) => (i === idx ? { ...el, [field]: value } : el)));
  }, [setWindows]);

  const handleAddWindow = useCallback(() => {
  setWindows((ws) => {
    if (!ws || ws.length === 0) {
      // prima finestra: usa lo stato iniziale
      return [...ws, INITIAL_WINDOW()];
    }
    // clona SEMPRE l'ultima finestra
    const last = ws[ws.length - 1];
    return [...ws, cloneWindow(last)];
  });
}, [setWindows]);


  const handleRemoveWindow = useCallback((idx) => {
    setWindows(ws => ws.filter((_, i) => i !== idx));
  }, [setWindows]);



const duplicateWindowAt = useCallback((idx) => {
  setWindows((ws) => {
    const base = ws[idx];
    if (!base) return ws;
    const copy = cloneWindow(base);
    const arr = ws.slice();
    arr.splice(idx + 1, 0, copy);
    return arr;
  });
}, [setWindows]);




  // debug
  useEffect(() => {
    console.log('🧩 id rilevatore (AuthContext):', idRilevatore);
  }, [idRilevatore]);

  // ⬇️ (il prossimo blocco parte da: // Mappa una finestra in una “riga” per il pricing …)








    // Mappa una finestra in una “riga” per il pricing
  function mapWindowToRiga(w) {
    const qZanza = Number(w.zanza) || 0; // 0/1 nel tuo UI (toggle)
    const tipoKey = tipoKeyOf(w.type);
    return {
      tipo: tipoKey,
      qty: Number(w.quantity) || 1,
      scuri: Number(w.scuri) || 0,
      pers:  Number(w.pers)  || 0,
      zanza: qZanza,
      zanzariera: qZanza,
      allar: Number(w.allar) || 0,
      color1Id: w.color_id ? Number(w.color_id) : null,
      color2Id: w.col2_id  ? Number(w.col2_id)  : null,
      flags: {
        zonaF: w.zona === 'F',
        tapparella: !!w.tapp,
        cassonetto: !!w.cas,
      },
    };
  }

  // Calcoli DB (righe + totale listino) con try/catch
  const totalsDB = useMemo(() => {
    if (!listino) {
      return { perRiga: [], subtotale: 0, fissi: 0, dueColoriOnce: 0, totale: 0 };
    }
    try {
      const righe = windows.map(mapWindowToRiga);
      const dueColori = windows.some(
        w => w.color_id && w.col2_id && String(w.color_id) !== String(w.col2_id)
      );
      return pricePreventivo(righe, listino, colors, { dueColoriOnce: dueColori });
    } catch (e) {
      console.warn('[pricePreventivo error]', e);
      return { perRiga: [], subtotale: 0, fissi: 0, dueColoriOnce: 0, totale: 0 };
    }
  }, [windows, listino, colors]);

  // Totali UI (aggiunge costi extra)
// Totali UI (aggiunge costi extra) — COMMA-SAFE
const prezziUI = useMemo(() => {
  const dueColoriOnce = Number(totalsDB?.dueColoriOnce || 0);

  const extraSum = (extraCosts || []).reduce(
    (sum, c) => sum + toNumber(c.amount),
    0
  );

  const totale = toNumber(totalsDB?.totale || 0) + extraSum;

  return {
    basic:  totale * 0.5,
    target: totale * 1.0,
    evoplus: totale * 2.0,
    supplementoCol2: dueColoriOnce,
  };
}, [totalsDB, extraCosts]);


  // ⚠️ Controllo colori obbligatori (usato su Salva / PDF)
  const checkColorsOrWarn = useCallback((silent = false) => {
    const idx = (windows || []).findIndex(
      (w) => !w?.color_id || !(parseInt(w.color_id, 10) > 0)
    );
    if (idx !== -1) {
      if (!silent) {
        alert('Inserisci i colori della finestra!');
        try {
          const cards = document.querySelectorAll('.window-card');
          cards[idx]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const sel = cards[idx]?.querySelector('.flag-color1 select.sbi-field');
          sel?.focus();
        } catch {}
      }
      return false;
    }
    return true;
  }, [windows]);

  // Costruisce il payload di UPDATE con legami event/zona
  function buildUpdatePayload({
    preventivoId,
    nomeCliente,
    localitaCliente,
    telefono,
    note,
    idRilevatore,   // ID effettivo del rilevatore
    eventId,        // opzionale: lega al record evento
    zonaId,         // opzionale: lega alla zona
    windows,
    extraCosts,
    basic,
    target,
    evoplus
  }) {
    return {
cliente: {
  id: preventivoId,
  nome: (nomeCliente || '').trim(),
  localita: (localitaCliente || '').trim(),
  telefono: (telefono || '').trim(),
  indirizzo: (indirizzoCliente || '').trim(),
  note: note || '',
  basic:  toInt(basic || 0),
  target: toInt(target || 0),
  evoplus: toInt(evoplus || 0),
  rilevatore: Number(idRilevatore) || null,
},


      event_id: eventId != null ? Number(eventId) : null,
      zona_id:  zonaId  != null ? Number(zonaId)  : null,
      windows: (windows || []).map(w => ({
        tipo:         w.type,
        quantita:     Math.max(1, parseInt(w.quantity ?? 1, 10) || 1),
        lar:          parseInt(w.lar, 10) || 0,
        alt:          parseInt(w.alt, 10) || 0,
        zona:         w.zona,
        color_id:     toNullableInt(w.color_id),
        col2_id:      toNullableInt(w.col2_id),
        tapp:         w.tapp ? 1 : 0,
        cas:          w.cas  ? 1 : 0,
        scuri:        parseInt(w.scuri, 10) || 0,
        pers:         parseInt(w.pers, 10)  || 0,
        zanza:        parseInt(w.zanza, 10) || 0,
        allar:        parseInt(w.allar, 10) || 0,
        nome_infisso: w.nomeInfisso || '',
        photos:       Array.isArray(w.photos) ? w.photos : [],
      })),
    extraCosts: (extraCosts || []).map(c => ({
  descrizione: (c.description || '').trim(),
  importo: toNumber(c.amount),
})),

    };
  }

  /* ─────────────────────────────────────────────────────────────
     INVIO RIEPILOGO AL BACKEND AGENDA (JSON o multipart con file)
     ───────────────────────────────────────────────────────────── */
  const salvaPreventivo = useCallback(async ({
  targetEuro,
  budgetEuro,
  planScelto,
  passareAlSenior,
  fullCalculationObject,
  pdfFile,
  audioFile,
  silent = false,
}) => {
  const t = getTokenForSync();
  if (!t) {
    if (!silent) console.info('[Agenda] nessun token disponibile: salto la sync.');
    return;
  }

  const endpoint = '/preventivi/agenda/api/receive_preventivo.php';
  const target = toInt(targetEuro);
  const budget = toInt(budgetEuro);
  const plan   = String(planScelto || '').toUpperCase(); // BASIC|TARGET|EVOPLUS

  try {
    if (!pdfFile && !audioFile) {
      const body = {
        target_eur: target,      // 🔒 interi “comma safe”
        budget_eur: budget,      // 🔒 interi “comma safe”
        plan,
        pass_to_senior: passareAlSenior ? 1 : 0,
        extra: fullCalculationObject || null,
      };

      const res = await fetch(`${endpoint}?t=${encodeURIComponent(t)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.status !== 'OK') {
        throw new Error(j.message || `Errore salvataggio preventivo (HTTP ${res.status})`);
      }
      return j;
    }

    // multipart
    const fd = new FormData();
    fd.append('t', t);
    fd.append('target_eur', String(target));
    fd.append('budget_eur', String(budget));
    fd.append('plan', plan);
    fd.append('pass_to_senior', passareAlSenior ? '1' : '0');
    if (fullCalculationObject) {
      fd.append('extra', new Blob([JSON.stringify(fullCalculationObject)], { type: 'application/json' }));
    }
    if (pdfFile)   fd.append('pdf',   pdfFile);
    if (audioFile) fd.append('audio', audioFile);

    const res = await fetch(endpoint, { method: 'POST', credentials: 'same-origin', body: fd });
    const j = await res.json().catch(() => ({}));
    if (!res.ok || j.status !== 'OK') {
      throw new Error(j.message || `Errore salvataggio preventivo (HTTP ${res.status})`);
    }
    return j;
  } catch (err) {
    console.error('[receive_preventivo ❌]', err);
    if (!silent) alert(String(err?.message || err));
    throw err;
  }
}, []);


  /* ─────────────────────────────────────────────────────────────
     SYNC verso Agenda: chiama salvaPreventivo se ha senso farlo
     ───────────────────────────────────────────────────────────── */


// Evita sync ripetuti identici









  /* ─────────────────────────────────────────────────────────────
   SYNC verso Agenda: chiama salvaPreventivo se ha senso farlo
   ───────────────────────────────────────────────────────────── */
const syncAgenda = useCallback(async () => {
  // evita popup fastidiosi durante autosave se manca il token
  const hasToken = !!(getTokenFromUrl() || localStorage.getItem('preventivo.jwt'));
  if (!hasToken) return;

  // invia solo se abbiamo un TARGET > 0 e almeno i campi base
  const targetVal = toInt(prezziUI?.target || 0);
  if (targetVal <= 0) return;
  if (!nomeCliente?.trim() || !localitaCliente?.trim()) return;

  try {
    await salvaPreventivo({
      targetEuro: targetVal,     // <<< usa SEMPRE targetVal
      budgetEuro: 0,
      planScelto: 'TARGET',
      passareAlSenior: false,
      fullCalculationObject: {
        linkage,
        preventivoId,
        cliente: {
          nome: nomeCliente,
          localita: localitaCliente,
          indirizzo: indirizzoCliente,
          telefono,
          note,
          rilevatoreId: effectiveRilevatoreId,
        },
        windows,
        extraCosts,
        totals: prezziUI,
      },
      pdfFile: null,
      audioFile: null,
      silent: true, // no popup in autosync
    });

    console.log('↔️ Agenda sync OK');
  } catch (e) {
    console.warn('↔️ Agenda sync fallita:', e?.message || e);
  }
}, [
  prezziUI, nomeCliente, localitaCliente, indirizzoCliente, telefono, note,
  linkage, preventivoId, effectiveRilevatoreId,
  windows, extraCosts, salvaPreventivo
]);

// Evita sync ripetuti identici (UNA SOLA DICHIARAZIONE nel file)
const lastSyncRef = useRef({ target: null, nome: '', loc: '' });

// Autosync “debounced” quando cambia target o dati minimi
useEffect(() => {
  const targetSync = toInt(prezziUI?.target || 0);
  const nome = (nomeCliente || '').trim();
  const loc  = (localitaCliente || '').trim();

  const hasToken = !!(getTokenFromUrl() || localStorage.getItem('preventivo.jwt'));
  if (!hasToken) return;
  if (targetSync <= 0 || !nome || !loc) return;

  const prev = lastSyncRef.current;
  if (prev.target === targetSync && prev.nome === nome && prev.loc === loc) return;

  const h = setTimeout(() => {
    syncAgenda()
      .then(() => { lastSyncRef.current = { target: targetSync, nome, loc }; })
      .catch(() => {});
  }, 700); // debounce

  return () => clearTimeout(h);
}, [
  prezziUI?.target,
  nomeCliente,
  localitaCliente,
  indirizzoCliente,
  windows,
  extraCosts,
  linkage?.eventId,
  linkage?.zonaId,
  effectiveRilevatoreId,
  syncAgenda
]);


  /* ─────────────────────────────────────────────────────────────
     Salvataggio automatico — AUTOSAVE + sync Agenda
     ───────────────────────────────────────────────────────────── */
  const salvaPreventivoAuto = useCallback(async () => {
    const trimmedNome = (nomeCliente || '').trim();
    const trimmedLoc  = (localitaCliente || '').trim();
    const trimmedTel  = (telefono || '').trim();
    const idRil       = effectiveRilevatoreId != null ? Number(effectiveRilevatoreId) : null;

    if (!preventivoId) {
      throw new Error('Nessun ID preventivo: usa “PDF Server” o “Salva” per creare il preventivo.');
    }
    if (!trimmedNome || !trimmedLoc || !trimmedTel) {
      throw new Error('Dati cliente incompleti (nome, località, telefono).');
    }

    const url = '/preventivi/aggiorna_preventivo.php';

    const payload = buildUpdatePayload({
      preventivoId,
      nomeCliente,
      localitaCliente,
      telefono: trimmedTel,
      note,
      idRilevatore: idRil,
      eventId: linkage.eventId != null ? Number(linkage.eventId) : null,
      zonaId:  linkage.zonaId  != null ? Number(linkage.zonaId)  : null,
      windows,
      extraCosts,
      basic:  prezziUI.basic,
      target: prezziUI.target,
      evoplus: prezziUI.evoplus,
    });

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload),
    });

    const text = await res.text();
    const json = (() => { try { return JSON.parse(text); } catch { return { status:'ERR', message:text }; } })();
    if (json.status !== 'OK') throw new Error(json.message || 'Errore autosave');

    // sync silenziosa verso Agenda (se c’è token)
    try { await syncAgenda(); } catch {}

    return true;
  }, [
    preventivoId,
    nomeCliente,
    localitaCliente,
    telefono,
    note,
    effectiveRilevatoreId,
    linkage.eventId,
    linkage.zonaId,
    windows,
    extraCosts,
    prezziUI,
    syncAgenda
  ]);

  /* ─────────────────────────────────────────────────────────────
     Salva (CREATE/UPDATE) con controllo colori
     ───────────────────────────────────────────────────────────── */


     const handleSave = useCallback(async () => {
  // validazione minima
  if (!nomeCliente.trim() || !localitaCliente.trim()) {
    alert('⚠️ Inserisci almeno Nome e Località');
    return null;
  }

  // controllo colori su azione manuale
  if (!checkColorsOrWarn(false)) return null;

  // UPDATE
  if (preventivoId && Number(preventivoId) > 0) {
    try {
      await salvaPreventivoAuto();
      alert('Salvato ✅');
      return Number(preventivoId) || null;
    } catch (e) {
      alert('Salvataggio fallito: ' + (e?.message || e));
      return null;
    }
  }

  // CREATE
  try {
    const createPayload = {
      nome: nomeCliente.trim(),
      localita: localitaCliente.trim(),
      telefono: (telefono || '').trim(),
      note,
      rilevatore: Number(effectiveRilevatoreId) || null,
      event_id: linkage.eventId != null ? Number(linkage.eventId) : null,
      zona_id:  linkage.zonaId  != null ? Number(linkage.zonaId)  : null,
      windows: windows.map(w => ({
        tipo: String(w.type || ''),
        quantita: parseInt(w.quantity ?? 1, 10) || 1,
        lar: parseInt(w.lar, 10) || 0,
        alt: parseInt(w.alt, 10) || 0,
        zona: String(w.zona || 'E'),
        color_id: toNullableInt(w.color_id),
        col2_id:  toNullableInt(w.col2_id),
        tapp: w.tapp ? 1 : 0,
        cas:  w.cas  ? 1 : 0,
        scuri: parseInt(w.scuri, 10) || 0,
        pers:  parseInt(w.pers, 10)  || 0,
        zanza: parseInt(w.zanza, 10) || 0,
        allar: parseInt(w.allar, 10) || 0,
        nome_infisso: String(w.nomeInfisso || ''),
        photos: Array.isArray(w.photos) ? w.photos : []
      })),
      extraCosts: (extraCosts || []).map(c => ({
        descrizione: String((c.description || '').trim()),
        importo: toNumber(c.amount),
      })),
      basic:  toInt(prezziUI.basic || 0),
      target: toInt(prezziUI.target || 0),
      evoplus: toInt(prezziUI.evoplus || 0),
    };

    const res = await fetch('/preventivi/save_preventivo.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(createPayload),
    });

    const text = await res.text();
    let json;
    try { json = JSON.parse(text); } catch { json = { status: 'ERR', message: text }; }
    if (json.status !== 'OK') throw new Error(json.message || 'Errore salvataggio');

    const newId = Number(json.id);
    if (!newId) throw new Error('ID non restituito dal server');

    setPreventivoId(newId);

    // Sync Agenda post-CREATE
    try {
      const hasToken = !!(getTokenFromUrl() || localStorage.getItem('preventivo.jwt'));
      const targetVal = toInt(prezziUI?.target || 0);

      if (hasToken && targetVal > 0) {
        await salvaPreventivo({
          targetEuro: targetVal,
          budgetEuro: 0,
          planScelto: 'TARGET',
          passareAlSenior: false,
          fullCalculationObject: {
            linkage,
            preventivoId: newId,
            cliente: {
              nome: nomeCliente,
              localita: localitaCliente,
              telefono,
              indirizzo: indirizzoCliente,
              note,
              rilevatoreId: effectiveRilevatoreId,
            },
            windows,
            extraCosts,
            totals: prezziUI,
          },
          pdfFile: null,
          audioFile: null,
          silent: true,
        });
      }
    } catch (e) {
      console.warn('Sync Agenda post-CREATE fallita:', e?.message || e);
    }

    alert('Creato e salvato ✅');
    return newId;
  } catch (e) {
    alert('Salvataggio fallito: ' + (e?.message || e));
    return null;
  }
}, [
  preventivoId,
  nomeCliente,
  localitaCliente,
  indirizzoCliente,
  telefono,
  note,
  windows,
  extraCosts,
  prezziUI,
  effectiveRilevatoreId,
  linkage,
  setPreventivoId,
  salvaPreventivo,
  salvaPreventivoAuto,
  checkColorsOrWarn
]);


  /* ─────────────────────────────────────────────────────────────
     Salva e apri PDF (server-side) + sync Agenda post-salvataggio
     ───────────────────────────────────────────────────────────── */
  const handleSaveAndOpenPdf = useCallback(async () => {
    if (!nomeCliente.trim() || !localitaCliente.trim()) {
      alert('⚠️ Inserisci nome e località');
      return;
    }

    if (!checkColorsOrWarn()) return;

    const isUpdate = !!preventivoId && Number(preventivoId) > 0;
    const url = isUpdate
      ? '/preventivi/aggiorna_preventivo.php'
      : '/preventivi/save_preventivo.php';

    // Payload CREATE
    const createPayload = {
      nome: nomeCliente.trim(),
      localita: localitaCliente.trim(),
      telefono: (telefono || '').trim(),
      note,
      rilevatore: Number(effectiveRilevatoreId) || null,
      event_id: linkage.eventId != null ? Number(linkage.eventId) : null,
      zona_id:  linkage.zonaId  != null ? Number(linkage.zonaId)  : null,
      windows: windows.map(w => ({
        tipo: String(w.type || ''),
        quantita: parseInt(w.quantity ?? 1, 10) || 1,
        lar: parseInt(w.lar, 10) || 0,
        alt: parseInt(w.alt, 10) || 0,
        zona: String(w.zona || 'E'),
        color_id: w.color_id != null ? (parseInt(w.color_id, 10) || null) : null,
        col2_id:  w.col2_id  != null ? (parseInt(w.col2_id, 10)  || null) : null,
        tapp: w.tapp ? 1 : 0,
        cas:  w.cas  ? 1 : 0,
        scuri: parseInt(w.scuri, 10) || 0,
        pers:  parseInt(w.pers, 10)  || 0,
        zanza: parseInt(w.zanza, 10) || 0,
        allar: parseInt(w.allar, 10) || 0,
        nome_infisso: String(w.nomeInfisso || ''),
        photos: Array.isArray(w.photos) ? w.photos : []
      })),
   extraCosts: (extraCosts || []).map(c => ({
  descrizione: String((c.description || '').trim()),
  importo: toNumber(c.amount),
})),

  basic:  toInt(prezziUI.basic || 0),
target: toInt(prezziUI.target || 0),
evoplus: toInt(prezziUI.evoplus || 0),

    };

    // UPDATE usa helper
    const payload = isUpdate
      ? buildUpdatePayload({
          preventivoId,
          nomeCliente,
          localitaCliente,
          telefono: (telefono || '').trim(),
          note,
          idRilevatore: Number(effectiveRilevatoreId) || null,
          eventId: linkage.eventId != null ? Number(linkage.eventId) : null,
          zonaId:  linkage.zonaId  != null ? Number(linkage.zonaId)  : null,
          windows,
          extraCosts,
          basic:  prezziUI.basic,
          target: prezziUI.target,
          evoplus: prezziUI.evoplus
        })
      : createPayload;

    // Apri subito la finestra (evita popup-blocker)
    const pdfWin = window.open('about:blank', '_blank');

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });

      const text = await res.text();
      let json;
      try { json = JSON.parse(text); } catch { json = { status: 'ERR', message: text }; }
      if (json.status !== 'OK') throw new Error(json.message || 'Errore generico');

      const savedId = isUpdate ? preventivoId : json.id;
      if (!savedId) throw new Error('ID preventivo non restituito dal server');

      if (!isUpdate) setPreventivoId(Number(savedId));

      // Sync Agenda SOLO se token valido
      try {
        const tokenForSync = getTokenForSync();
        const targetVal = toInt(prezziUI?.target || 0);

        if (tokenForSync && targetVal > 0 && nomeCliente.trim() && localitaCliente.trim()) {
          await salvaPreventivo({
            targetEuro: targetVal,
            budgetEuro: 0,
            planScelto: 'TARGET',
            passareAlSenior: false,
            fullCalculationObject: {
              linkage,
              preventivoId: Number(savedId),
              cliente: {
                nome: nomeCliente,
                localita: localitaCliente,
                indirizzo: indirizzoCliente,
                telefono,
                note,
                rilevatoreId: effectiveRilevatoreId,
              },
              windows,
              extraCosts,
              totals: prezziUI,
            },
            pdfFile: null,
            audioFile: null,
            silent: true,
          });
        }
      } catch (e) {
        console.warn('↔️ Sync Agenda post-salvataggio fallita:', e?.message || e);
      }

      // Apri/porta la finestra sul PDF
      const pdfUrl = `/preventivi/generapdf.php?quoteId=${savedId}`;
      setTimeout(() => {
        if (pdfWin && !pdfWin.closed) {
          try { pdfWin.location.replace(pdfUrl); }
          catch { window.open(pdfUrl, '_blank', 'noopener,noreferrer'); }
        } else {
          window.open(pdfUrl, '_blank', 'noopener,noreferrer');
        }
      }, 50);
    } catch (err) {
      console.error('[SAVE+PDF ❌]', err);
      alert('❌ Salvataggio o generazione PDF falliti: ' + (err.message || err));
      if (pdfWin && !pdfWin.closed) pdfWin.close();
    }
  }, [
    preventivoId,
    nomeCliente,
    localitaCliente,
    indirizzoCliente,
    telefono,
    note,
    windows,
    extraCosts,
    prezziUI,
    effectiveRilevatoreId,
    linkage,
    setPreventivoId,
    salvaPreventivo,
    checkColorsOrWarn
  ]);

  // ─── Early returns ───────────────────────────────────────────
  if (loading) return <p style={{ padding: 20 }}>Caricamento…</p>;
  if (error) {
    return (
      <p style={{ color: 'red', padding: 20 }}>Errore: {error}</p>
    );
  }

  // ─── RENDER ──────────────────────────────────────────────────
  return (
    <UiErrorBoundary>
      <div className="App nuovo-page" ref={captureRef}>

        {/* HEADER 2 righe × 3 colonne */}
        <div className="header-grid">
          {/* ───── RIGA 1 ───── */}
          <div className="header-cell anagrafica">
            <label
              className="sbi"
              style={{
                backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/cliente.png")`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center'
              }}
            >
              <input
                type="text"
                className="sbi-field"
                value={nomeCliente}
                onChange={(e) => setNomeCliente(e.target.value)}
                placeholder="Nome Cliente"
              />
            </label>
          </div>

          <div className="header-cell logo">
            <img src={logo} alt="Logo Infissi" className="logo-img" />
          </div>

          <div className="header-cell anagrafica localita-con-geo">
            <label
              className="sbi"
              style={{
                backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/localita.png")`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center'
              }}
            >
              <input
                type="text"
                className="sbi-field"
                value={localitaCliente}
                onChange={(e) => setLocalitaCliente(e.target.value)}
                placeholder="Località"
              />
            </label>

            <button
              type="button"
              className="geo-button-inside"
              onClick={handleGeolocalizza}
              title="Geolocalizza"
              aria-label="Geolocalizza"
            >
              📍
            </button>
          </div>

          {/* ───── RIGA 2 ───── */}
          <div className="header-cell anagrafica">
            <label
              className="sbi"
              style={{
                backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/telefono.png")`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center'
              }}
            >
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                autoComplete="tel"
                className="sbi-field"
                value={telefono ?? ''}
                onChange={(e) =>
                  setTelefono(e.target.value.replace(/\D/g, '').slice(0, 10))
                }
                placeholder="Telefono"
              />
            </label>
          </div>

          <div className="header-cell intestazione">
            <span className="titolo-rilievo">Rilievo Misure</span>
          </div>

          <div className="header-cell rilevatore-statico-wrapper">
            <div
              className="sbi"
              style={{
                backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/note.png")`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center'
              }}
            >
              <div className="rilevatore-info">
                <strong style={{ fontSize: '0.5rem' }}>R:</strong>{' '}
                {rilevatoreSelezionato || '—'}
              </div>
            </div>
          </div>
        </div>

        {/* PULSANTE AGGIUNGI (sopra) */}
        <div className="add-button-wrapper">
          <button className="add-window" onClick={handleAddWindow}>
            ➕ Aggiungi Finestra
          </button>
        </div>

        {/* === CARDS FINESTRE ================================================== */}
        {windows.map((w, idx) => (
          <div className="window-card" key={w.id || `w-${idx}-${w.type || 'X'}-${w.lar || 0}-${w.alt || 0}`}>
            {/* HEADER riga finestra: badge + totale/messaggio colori */}
            {(() => {
              const riga = mapWindowToRiga(w);
              const hasColor1 = !!(w?.color_id && parseInt(w.color_id, 10) > 0);

              let rigaTot = 0;
              let priceErr = null;

              if (listino && hasColor1) {
                try {
                  rigaTot = priceLine(riga, listino, colors);
                } catch (e) {
                  priceErr = e;
                  console.warn('[priceLine error]', e);
                }
              }

              return (
                <div className="window-header-row">
                  <div
                    className="window-number-badge"
                    style={{ backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/posizione.png")` }}
                  >
                    <span>{idx + 1}</span>
                  </div>

                  {!hasColor1 ? (
                    <div className="riga-price" style={{ color: '#c0392b', fontWeight: 700 }}>
                      Inserisci i colori della finestra!
                    </div>
                  ) : priceErr ? (
                    <div className="riga-price" style={{ color: '#c0392b', fontWeight: 700 }}>
                      Errore nel calcolo. Controlla i dati.
                    </div>
                  ) : (
                    <div className="riga-price">
                      Totale riga: € {rigaTot.toFixed(2)}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* 1) MISURE */}
            <div className="dims-row">
              {/* Tipo (select) */}
              <div className="btn-wrapper type">
                <label
                  className="sbi"
                  style={{
                    backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/tipo.png")`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center'
                  }}
                >
                  <select
                    id={`type-${idx}`}
                    className="sbi-field"
                    aria-label="Tipo infisso"
                    value={w.type ?? ''}
                    onChange={(e) => updateField(idx, 'type', e.target.value)}
                    required
                  >
                    <option value="" disabled hidden>– seleziona –</option>

                    {/* Opzioni dinamiche dal listino */}
                    {Object.entries(groupedTipoOptions).map(([label, items]) => (
                      <optgroup key={label} label={label}>
                        {items.map((t) => {
                          const meta = getTypeMeta(t);
                          return (
                            <option key={tipoKeyOf(t)} value={t}>
                              {meta.label}
                            </option>
                          );
                        })}
                      </optgroup>
                    ))}

                    {/* Se il tipo salvato non è tra le opzioni (caso edge), mostralo comunque */}
                    {!tipoOptions.includes(String(w.type || '')) && w.type && (
                      <optgroup label="(salvato)">
                        <option value={w.type}>{getTypeMeta(w.type).label}</option>
                      </optgroup>
                    )}
                  </select>
                </label>
              </div>

              {/* Zona (select) */}
              <div className="btn-wrapper zone">
                <label
                  className="sbi"
                  style={{
                    backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/zona.png")`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center'
                  }}
                >
                  <select
                    className="sbi-field"
                    value={w.zona ?? ''}
                    onChange={(e) => updateField(idx, 'zona', e.target.value)}
                  >
                    <option value="">– seleziona –</option>
                    <option value="E">E</option>
                    <option value="F">F</option>
                  </select>
                </label>
              </div>

              {/* Larghezza */}
              <div className="btn-wrapper larghezza">
                <label
                  className="sbi"
                  style={{
                    backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/larghezza.png")`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center'
                  }}
                >
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="sbi-field"
                    value={w.lar ?? ''}
                    onChange={(e) =>
                      updateField(
                        idx,
                        'lar',
                        e.target.value.replace(/\D/g, '').slice(0, 4)
                      )
                    }
                    placeholder="mm"
                  />
                </label>
              </div>

              {/* Altezza */}
              <div className="btn-wrapper altezza">
                <label
                  className="sbi"
                  style={{
                    backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/altezza.png")`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center'
                  }}
                >
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="sbi-field"
                    value={w.alt ?? ''}
                    onChange={(e) =>
                      updateField(
                        idx,
                        'alt',
                        e.target.value.replace(/\D/g, '').slice(0, 4)
                      )
                    }
                    placeholder="mm"
                  />
                </label>
              </div>
            </div>

            {/* 2) RIGA ACCESSORI (Colore 1 + toggle) */}
            <div className="second-row" role="group" aria-label="Accessori riga 2">
              {/* Colore 1 */}
              <div className="btn-wrapper flag-color1">
                <label
                  className="sbi"
                  style={{ backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/colore1.png")` }}
                >
                  <select
                    className="sbi-field"
                    value={w.color_id ?? ''}
                    onChange={e => updateField(idx, 'color_id', e.target.value || '')}
                  >
                    <option value="">—col1—</option>
                    {colors.map(c => (
                      <option key={c.id} value={String(c.id)}>{c.name}</option>
                    ))}
                  </select>
                </label>
              </div>

              {/* Tapparella */}
              <div className="btn-wrapper flag-tapparella">
                <button
                  type="button"
                  className="toggle-flag-button"
                  aria-pressed={!!w.tapp}
                  onClick={() => updateField(idx, 'tapp', !w.tapp)}
                  title="Tapparella"
                  style={{
                    backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/${w.tapp ? 'tapparellaon' : 'tapparellaoff'}.png")`
                  }}
                />
              </div>

              {/* Zanzariera */}
              <div className="btn-wrapper flag-zanzariera">
                <button
                  type="button"
                  className="toggle-flag-button"
                  aria-pressed={Number(w.zanza) === 1}
                  onClick={() => updateField(idx, 'zanza', Number(w.zanza) === 1 ? 0 : 1)}
                  title="Zanzariera"
                  style={{
                    backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/${Number(w.zanza) === 1 ? 'zanzarieraon' : 'zanzarieraoff'}.png")`
                  }}
                />
              </div>

              {/* Cassonetto */}
              <div className="btn-wrapper flag-cassonetto">
                <button
                  type="button"
                  className="toggle-flag-button"
                  aria-pressed={!!w.cas}
                  onClick={() => updateField(idx, 'cas', !w.cas)}
                  title="Cassonetto"
                  style={{
                    backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/${w.cas ? 'cassonettoon' : 'cassonettooff'}.png")`
                  }}
                />
              </div>
            </div>

            {/* 3) RIGA Colore 2 + accessori numerici */}
            <div className="third-row" role="group" aria-label="Colore 2 e accessori">
              {/* Colore 2 */}
              <div className="btn-wrapper flag-color2">
                <label
                  className="sbi"
                  style={{ backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/colore2.png")` }}
                >
                  <select
                    className="sbi-field"
                    value={w.col2_id ?? ''}
                    onChange={e => updateField(idx, 'col2_id', e.target.value || '')}
                  >
                    <option value="">—col2-</option>
                    {colors.map(c => (
                      <option key={c.id} value={String(c.id)}>{c.name}</option>
                    ))}
                  </select>
                </label>
              </div>

              {/* Scuri (max 4) */}
              <AccessoryCapsule
                className="scuri"
                img={`${process.env.PUBLIC_URL}/ImmaginiBottoni/scuri.png`}
                value={w.scuri}
                max={4}
                onChange={v => updateField(idx, 'scuri', v)}
              />

              {/* Allargatori (max 8) */}
              <AccessoryCapsule
                className="allargatori"
                img={`${process.env.PUBLIC_URL}/ImmaginiBottoni/allargatore.png`}
                value={w.allar}
                max={8}
                onChange={v => updateField(idx, 'allar', v)}
              />

              {/* Persiane (max 4) */}
              <AccessoryCapsule
                className="persiane"
                img={`${process.env.PUBLIC_URL}/ImmaginiBottoni/persiana.png`}
                value={w.pers}
                max={4}
                onChange={v => updateField(idx, 'pers', v)}
              />
            </div>

            {/* 4) PREVIEW DELL’INFISSO + ACCESSORI */}
            <div className="window-preview-row scroll-x">
              {(() => {
                const imgSrc = previewImg(w.type);
                return (imgSrc && parseInt(w.quantity, 10) > 0)
                  ? <img className="image-preview" src={imgSrc} alt={w.nomeInfisso || w.type || 'infisso'} />
                  : null;
              })()}

              {w.zona === 'F' && <img className="image-preview" src={triploVetroImage} alt="Triplo Vetro" />}
              {w.tapp && <img className="image-preview" src={imgTapparella} alt="Tapparella" />}
              {w.cas  && <img className="image-preview" src={imgCassonetto} alt="Cassonetto" />}
              {parseInt(w.zanza, 10) > 0 && <img className="image-preview" src={imgZanzariera} alt="Zanzariera" />}
              {parseInt(w.scuri, 10) > 0 && <img className="image-preview" src={imgScuri} alt="Scuri" />}
              {parseInt(w.pers, 10)  > 0 && <img className="image-preview" src={imgPersiana} alt="Persiana" />}
            </div>

            {/* 5) BLOCCHI POS, NOME INFISSO E CESTINO */}
            <div className="window-footer-row">
              <div
                className="posizione-grafica"
                style={{
                  backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/posizione.png")`,
                  '--pos-numero-size': '0.8rem',
                  '--pos-numero-color': 'red'
                }}
              >
                <span className="pos-numero">{idx + 1}</span>
              </div>

              {/* Input nome finestra */}
              <div className="nome-infisso-wrapper">
                <input
                  type="text"
                  className="nome-infisso-input"
                  maxLength={15}
                  placeholder="Es. cucina1"
                  value={w.nomeInfisso}
                  onChange={e => {
                    const val = e.target.value;
                    setWindows(ws => ws.map((w,i) => i === idx ? { ...w, nomeInfisso: val } : w));
                  }}
                  onBlur={() => { salvaPreventivoAuto().catch(()=>{}); }}
                />
              </div>



<button
  type="button"
  className="duplicate-window-btn"
  title="Duplica questa finestra"
  aria-label="Duplica questa finestra"
  onClick={() => duplicateWindowAt(idx)}
  style={{ backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/duplica.png")` }}
/>






              {/* Cestino */}
              <button
                type="button"
                className="remove-window-btn"
                onClick={() => handleRemoveWindow(idx)}
                title="Elimina questa finestra"
                style={{ backgroundImage: `url("${process.env.PUBLIC_URL}/ImmaginiBottoni/cestino.png")` }}
              />
            </div>

            {/* 6) FOTO + PREVIEW COLORI (30%-40%-30%) */}
            <div className="add-photo-wrapper">
              {/* Colore 1 (30%) */}
              <div className="color-preview-half">
                {getColorPreviewElement(
                  getColorById(colors, w.color_id)?.code,
                  { fullWidth: true, noBorder: true }
                )}
              </div>

              {/* Pulsante “📷 Foto” (40%) */}
              <div className="photo-button-half">
                <button
                  className="add-photo-button"
                  onClick={() => fileInputs.current[idx]?.click()}
                >
                  📷 Foto
                </button>
              </div>

              {/* Colore 2 (30%) */}
              <div className="color-preview-half">
                {getColorPreviewElement(
                  getColorById(colors, w.col2_id)?.code,
                  { fullWidth: true, noBorder: true }
                )}
              </div>

              <input
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                ref={(el) => (fileInputs.current[idx] = el)}

                onChange={async (e) => {
  const input = e.target;
  const file = input.files?.[0];

  try {
    // 1) mi assicuro che il preventivo sia salvato
    let quoteId = preventivoId;

    if (!quoteId) {
      // provo a salvare automaticamente
      const savedId = await handleSave();   // ora ritorna l'ID o null
      quoteId = savedId || 0;
    }

    if (!quoteId) {
      // se anche dopo il tentativo di salvataggio non ho un ID, non ha senso procedere
      alert('Impossibile salvare il preventivo prima di caricare la foto.');
      input.value = '';
      return;
    }

    // 2) da qui in poi è come prima, ma uso quoteId invece di preventivoId
    const already = Array.isArray(windows[idx]?.photos) ? windows[idx].photos.length : 0;
    if (already >= 3) {
      alert('Puoi caricare al massimo 3 foto per finestra.');
      input.value = '';
      return;
    }
    if (!file) return;
    if (!/^image\//i.test(file.type)) {
      alert('Seleziona un file immagine valido.');
      input.value = '';
      return;
    }

    const options = { maxSizeMB: 0.5, maxWidthOrHeight: 1024, useWebWorker: true };
    const compressed = await imageCompression(file, options);

    const form = new FormData();
    const safeName = file.name.replace(/\s+/g, '_');
    form.append('photo', compressed, safeName);
    form.append('quoteId', String(quoteId));  // 👈 QUI usiamo l'ID certo
    if (linkage?.eventId) form.append('eventId', String(linkage.eventId));
    if (effectiveRilevatoreId) form.append('rilevatoreId', String(effectiveRilevatoreId));
    form.append('windowIndex', String(idx));

    const res = await fetch('/preventivi/upload_photo.php', { method: 'POST', body: form });
    const text = await res.text();

    let json;
    try {
      json = JSON.parse(text);
    } catch {
      throw new Error(text || 'Risposta non valida dal server');
    }

    if (json.status !== 'OK') {
      throw new Error(json.message || 'Upload foto non riuscito');
    }

    const path = json.path || json.url || '';
    if (!path) throw new Error('Percorso immagine mancante nella risposta');

    setWindows((ws) => {
      const copy = [...ws];
      const prevPhotos = Array.isArray(copy[idx].photos) ? copy[idx].photos : [];
      copy[idx].photos = [...prevPhotos, path].slice(0, 3);
      return copy;
    });

    await new Promise((r) => setTimeout(r, 0));
    try { await salvaPreventivoAuto(); } catch {}
  } catch (err) {
    alert('Upload foto fallito: ' + (err.message || err));
  } finally {
    if (e.target) e.target.value = '';
  }
}}

              />
            </div>

            {/* 7) Anteprime delle FOTO CARICATE */}
            {w.photos?.length > 0 && (
              <div className="photo-list">
                {w.photos.map((photo, i) => (
                  <div key={i} className="photo-container">
                    <img src={getPhotoSrc(photo)} alt={`Foto ${i + 1}`} />
                    <button
                      className="photo-remove-btn"
                      onClick={() =>
                        setWindows(ws => ws.map((w,k) =>
                          k === idx ? { ...w, photos: (w.photos || []).filter((_,j) => j !== i) } : w
                        ))
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* PULSANTE AGGIUNGI (sotto) */}
        <button className="add-window" onClick={handleAddWindow}>
          ➕ Aggiungi Finestra
        </button>

        {/* COSTI EXTRA */}
        <div className="extra-costs">
          <h2>Costi Extra</h2>

          {extraCosts.map((c, i) => (
            <div key={i} className="extra-cost-row">
              <input
                type="text"
                placeholder="Descrizione"
                value={c.description ?? ''}
                onChange={(e) => {
                  const a = [...extraCosts];
                  a[i].description = e.target.value;
                  setExtraCosts(a);
                }}
              />

              <input
                type="number"
                placeholder="Importo"
                value={Number.isFinite(c.amount) ? c.amount : 0}
                min="0"
                max="9999"
                onChange={(e) => {
                  const a = [...extraCosts];
                  a[i].amount = parseFloat(e.target.value) || 0;
                  setExtraCosts(a);
                }}
              />

              {/* Cestino con <img> controllato dal CSS */}
              <button
                type="button"
                className="remove-cost"
                title="Rimuovi questo costo"
                aria-label="Rimuovi questo costo"
                onClick={() => setExtraCosts((ec) => ec.filter((_, idx) => idx !== i))}
              >
                <img
                  className="trash-icon"
                  alt=""
                  aria-hidden="true"
                  src={`${process.env.PUBLIC_URL}/ImmaginiBottoni/cestino.png`}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const p = e.currentTarget.parentElement;
                    p.textContent = '🗑️';
                    p.style.fontSize = '20px';
                    p.style.lineHeight = '1';
                  }}
                />
              </button>
            </div>
          ))}

          <button
            type="button"
            className="add-extra-cost-button"
            onClick={() =>
              setExtraCosts((ec) => [...ec, { description: '', amount: 0 }])
            }
          >
            ➕ Costo Extra
          </button>
        </div>

        {/* PULSANTI AZIONE */}
        <div className="action-buttons">
          <button type="button" className="btn pdf" onClick={handleSaveAndOpenPdf}>
            📄 PDF Server
          </button>
          <button type="button" className="btn save" onClick={handleSave}>
            💾 Salva
          </button>
          <button type="button" className="btn list" onClick={() => navigate('/elenco')}>
            📋 Elenco
          </button>
        </div>

        {/* TOTALI */}
        <div className="total compact">
          <div className="total-item">
            <img className="total-coin" src={coin} alt="" aria-hidden="true" />
            <strong>
              BASIC: {prezziUI.basic.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
            </strong>
          </div>

          <div className="total-item">
            <img className="total-coin" src={coin} alt="" aria-hidden="true" />
            <strong>
              TARGET: {prezziUI.target.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
            </strong>
          </div>

          <div className="total-item">
            <img className="total-coin" src={coin} alt="" aria-hidden="true" />
            <strong>
              EVOPLUS: {prezziUI.evoplus.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
            </strong>
          </div>

          {prezziUI.supplementoCol2 > 0 && (
            <div className="total-item" style={{ gridColumn: '1 / -1', color: 'crimson', fontWeight: 'bold' }}>
              Supplemento Colore2:{' '}
              {prezziUI.supplementoCol2.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
            </div>
          )}
        </div>

        {/* LOGOUT */}
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <button
            type="button"
            onClick={logout}
            style={{
              backgroundColor: '#e74c3c',
              color: 'white',
              padding: '10px 20px',
              fontSize: '1.1rem',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              width: '80%'
            }}
          >
            🔒 Logout
          </button>
        </div>

      </div>{/* ⬅️ chiude .App */}
    </UiErrorBoundary>
  );
} // ⬅️ chiude export default function Nuovo()
